// ============================================
// document-i18n.ts — R14 helper formatters for document i18n keys
// ============================================
//
// Thin wrappers around `useI18n()` that pre-format the plural /
// templated strings used by the document UI (peer count, last-saved).
// Keeping them out of the components lets us swap in a real i18n
// library later without grepping through JSX.
//
// The base `t()` in `@/lib/i18n` takes a single key + returns the raw
// string. We do tiny `{token}` substitution in the helpers so we
// don't have to extend the global t() signature for the MVP.

import { useI18n } from '@/lib/i18n';
import { formatDistanceToNow } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';

function getLocale(language: string) {
    return language === 'fr' ? fr : enUS;
}

function fillTemplate(template: string, params?: Record<string, string | number>): string {
    if (!params) return template;
    return Object.entries(params).reduce(
        (acc, [k, v]) => acc.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v)),
        template
    );
}

export function useDocumentI18n() {
    const { t, language } = useI18n();

    return {
        t: (key: string, params?: Record<string, string | number>) => fillTemplate(t(key as never), params),
        language,
        formatLastSavedAt(iso: string | null | undefined): string {
            if (!iso) return '';
            try {
                const date = new Date(iso);
                const distance = formatDistanceToNow(date, {
                    addSuffix: true,
                    locale: getLocale(language),
                });
                return fillTemplate(t('document.lastSavedAt' as never), { time: distance });
            } catch {
                return '';
            }
        },
        formatPeerCount(count: number): string {
            if (count === 0) return t('document.collab.zeroPeers' as never);
            if (count === 1) return t('document.collab.onePeer' as never);
            return fillTemplate(t('document.collab.peers' as never), { count });
        },
    };
}